import { useEffect, useState } from "react";

const MIN_WIDTH = 300;
const TOP_SHIFT = 0;
const LEFT_SHIFT = -6;
const NOT_FOUND_TOP_SHIFT = 24;
const NOT_FOUND_LEFT_SHIFT = 24;

export const guideSteps = {
  PROFILE: "PROFILE",
  NEW_BUTTON: "NEW_BUTTON",
  BUTTON: "BUTTON",
  BUTTON_QUESTION: "BUTTON_QUESTION",
  WEBSITE: "WEBSITE",
};

const firstStep = Object.values(guideSteps)[0];

const resetZIndex = () => {
  const request = Object.keys(guideSteps).map((key) => `.${guideSteps[key]}`);
  const targets = document.querySelectorAll(request);
  targets.forEach((target) => {
    target.style.zIndex = 0;
  });
};

const checkIfElementUnderScroll = (element) => {
  if (!element) {
    return false;
  }
  const rect = element.getBoundingClientRect();
  const viewHeight = Math.max(
    document.documentElement.clientHeight,
    window.innerHeight
  );
  return rect.top > (viewHeight / 4) * 3;
};

const isElementHidden = (element) => {
  if (!element) {
    return true;
  }
  
  let currentElement = element;
  while (currentElement) {
    const style = window.getComputedStyle(currentElement);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return true;
    }
    currentElement = currentElement.parentElement;
  }
  
  return false;
};

const findTargetByStepClass = (stepId) => {
  resetZIndex();
  const targets = document.querySelectorAll(`.${stepId}`);

  let target = [...targets].find((target) => {
    const checkVisibility = target?.checkVisibility;
    return typeof checkVisibility === "function"
      ? target.checkVisibility()
      : !isElementHidden(target);
  });

  if (!target) {
    target = targets[0];
  }

  if (target?.style) {
    target.style.zIndex = 50;
  }

  window.scrollTo(0, 0);

  if (checkIfElementUnderScroll(target)) {
    target?.scrollIntoView();
  }

  return target;
};

export const useOnboarding = ({ originator }) => {
  const defaultIsIntroFinished =
    window.localStorage.getItem(`${originator?.id}-is-onboarding-finished`) ===
    "true";
  const [isOnboardingFinished, setIsOnboardingFinished] = useState(
    defaultIsIntroFinished
  );
  const [currentStep, setCurrentStep] = useState(firstStep);
  const [onboardingTarget, setOnboardingTarget] = useState(null);
  const [posTop, setPosTop] = useState(0);
  const [posLeft, setPosLeft] = useState(0);

  const finishOnboarding = () => {
    window.localStorage.setItem(
      `${originator?.id}-is-onboarding-finished`,
      "true"
    );
    setIsOnboardingFinished(true);
    setOnboardingTarget(null);
    window.scrollTo(0, 0);
  };

  const nextStep = () => {
    const steps = Object.keys(guideSteps);
    const currentStepIndex = steps.indexOf(currentStep);

    if (currentStepIndex === steps.length - 1) {
      finishOnboarding();
      return;
    }

    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1]);
    }
  };

  const prevStep = () => {
    const steps = Object.keys(guideSteps);
    const currentStepIndex = steps.indexOf(currentStep);

    if (currentStepIndex === 0) {
      return;
    }

    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1]);
    }
  };

  const currentStepIndex = Object.values(guideSteps).indexOf(currentStep) + 1;
  const stepsLength = Object.values(guideSteps).length;

  const resizeHandler = (onboardingTarget) => {
    if (onboardingTarget?.tagName === "BODY") {
      setPosTop(NOT_FOUND_TOP_SHIFT);
      setPosLeft(NOT_FOUND_LEFT_SHIFT);
      return;
    }
    if (onboardingTarget) {
      const rect = onboardingTarget.getBoundingClientRect();
      let top = rect.top + TOP_SHIFT + window.scrollY;
      let left =
        rect.left + rect.width + LEFT_SHIFT + window.scrollX + TOP_SHIFT;
      const windowWidth = window.innerWidth;

      if (windowWidth - left < MIN_WIDTH) {
        top = rect.top + rect.height + window.scrollY + TOP_SHIFT;
        left = rect.left + window.scrollX + LEFT_SHIFT;
      }

      setPosTop(top > 0 ? top : NOT_FOUND_TOP_SHIFT);
      setPosLeft(left > 0 ? left : NOT_FOUND_LEFT_SHIFT);
    }
  };

  useEffect(() => {
    const resize = () => resizeHandler(onboardingTarget);
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
    };
  }, [onboardingTarget]);

  useEffect(() => {
    const target = findTargetByStepClass(currentStep);

    if (!target) {
      const body = document.querySelector("body");
      setOnboardingTarget(body);
      resizeHandler(body);
      return;
    }
    setOnboardingTarget(target);
    resizeHandler(target);
  }, [currentStep]);

  useEffect(() => {
    const onboardingMarker = document.getElementById("onboarding-marker");
    if (!onboardingMarker) return;

    if (checkIfElementUnderScroll(onboardingMarker)) {
      window.scrollTo(0, 0);
      onboardingMarker?.scrollIntoView();
    }
  }, [posTop]);

  return {
    isOnboardingFinished,
    onboardingTarget,
    currentStep,
    nextStep,
    prevStep,
    finishOnboarding,
    posTop,
    posLeft,
    currentStepIndex,
    stepsLength,
  };
};
